// client/src/components/ui/Toasts.tsx — the toast region · UI-003 (04 §5.9, §4.9, §10.3).
//
// Mounted once, in `main.tsx`, beside the router — a toast must survive the navigation that
// caused it („Plata a fost înregistrată" is raised as the page it was raised from unmounts).
//
// Two accessibility decisions that are not cosmetic:
//   • `role="status"` (polite) for success/info and `role="alert"` (assertive) for warning and
//     error — an assertive live region interrupts a screen reader mid-sentence, which is
//     right for a failure and rude for a confirmation (§10.3);
//   • the region is `aria-live` at the CONTAINER, which must therefore be in the DOM before
//     the first toast arrives — a live region created together with its content announces
//     nothing in most screen readers. Hence a region that renders empty.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '@/components/icons';
import { TOAST_DURATION_MS, useToastStore, type Toast as ToastModel } from '@/stores/toast-store';

import { Glyph, type GlyphTone } from './Glyph';

// The third visual round (D-1846) took the toast off DaisyUI's solid `alert` fill: a white card
// with a coloured edge, the icon in a tinted square, and a bar that drains over the toast's own
// duration so the reader can see how long it stays. Roles, timing, pause-on-hover and the undo
// slot are §5.9's and unchanged; the classes below are the only thing that moved.
const VARIANT_CLASS: Record<
  ToastModel['variant'],
  { edge: string; glyph: GlyphTone; bar: string }
> = {
  success: { edge: 'border-l-success', glyph: 'success', bar: 'bg-success' },
  info: { edge: 'border-l-info', glyph: 'info', bar: 'bg-info' },
  warning: { edge: 'border-l-warning', glyph: 'warning', bar: 'bg-warning' },
  error: { edge: 'border-l-error', glyph: 'danger', bar: 'bg-error' },
};

const VARIANT_ICON: Record<ToastModel['variant'], IconName> = {
  success: 'check-circle',
  info: 'information-circle',
  warning: 'exclamation-triangle',
  error: 'x-circle',
};

export function Toasts() {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);

  return (
    // z-[60] is the top of the §4.9 scale: a toast outranks the modal it may report on.
    <div
      className="pointer-events-none fixed top-4 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      // The aria-live the comment at the top of this file calls for and the region did not
      // carry. Without it a role="status" toast announces nothing in most screen readers:
      // status is polite, and a polite region that appears together with its content is
      // not read. role="alert" items are unaffected — alert is assertive on the element
      // itself, so an error still interrupts.
      aria-live="polite"
      aria-label={t('common.toastRegion')}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

function ToastItem({ toast }: { toast: ToastModel }) {
  const { t } = useTranslation();
  const dismiss = useToastStore((state) => state.dismiss);
  const [paused, setPaused] = useState(false);

  // The deadline is what pausing preserves: on resume the toast gets the REMAINING time, not
  // a fresh full duration, so hovering cannot keep a message alive indefinitely by accident.
  const remainingRef = useRef(TOAST_DURATION_MS[toast.variant]);
  const startedAtRef = useRef(Date.now());

  const close = useCallback(() => {
    dismiss(toast.id);
  }, [dismiss, toast.id]);

  useEffect(() => {
    if (paused) {
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startedAtRef.current),
      );
      return;
    }
    startedAtRef.current = Date.now();
    const timer = window.setTimeout(close, remainingRef.current);
    return () => {
      window.clearTimeout(timer);
    };
  }, [paused, close]);

  const assertive = toast.variant === 'error' || toast.variant === 'warning';

  return (
    <div
      className={`toast-card pointer-events-auto relative flex items-start gap-3 overflow-hidden rounded-xl border border-base-300/80 border-l-4 bg-base-100 py-3 pr-2 pl-3 shadow-lg ${VARIANT_CLASS[toast.variant].edge}`}
      style={{ ['--toast-duration' as string]: `${TOAST_DURATION_MS[toast.variant]}ms` }}
      data-paused={paused ? 'true' : undefined}
      role={assertive ? 'alert' : 'status'}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      // Focus anywhere inside pauses too: a keyboard user tabbing to „Anulează" must not have
      // the button disappear from under them (§10.5).
      onFocus={() => {
        setPaused(true);
      }}
      onBlur={() => {
        setPaused(false);
      }}
    >
      <Glyph
        icon={VARIANT_ICON[toast.variant]}
        tone={VARIANT_CLASS[toast.variant].glyph}
        size="sm"
      />
      <span className="min-w-0 flex-1 self-center text-sm text-pretty text-base-content">
        {toast.message}
      </span>
      {toast.action ? (
        <button type="button" className="btn btn-sm self-center" onClick={toast.action.onAction}>
          {toast.action.label}
        </button>
      ) : null}
      {/* The drain: a bar the width of the card that shrinks to nothing over the toast's own
          duration and holds while the toast is paused. Decorative — the timer above is the
          truth — and killed by the reduced-motion switch. */}
      <span
        className={`toast-bar absolute inset-x-0 bottom-0 h-0.5 origin-left ${VARIANT_CLASS[toast.variant].bar}`}
        aria-hidden="true"
      />

      <button
        type="button"
        className="btn btn-ghost btn-sm btn-square"
        onClick={close}
        aria-label={t('common.close')}
      >
        <Icon name="x-mark" className="h-4 w-4" />
      </button>
    </div>
  );
}
